import { TextInput } from "components/DesignSystem/TextInput/TextInput";
import { Form, Formik, FormikHelpers } from "formik";
import { brandCreationSchema } from "formValidations/multiEntityCreation";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useState, useEffect, useContext } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { ServiceTeamCreation } from "./ServiceTeam/ServiceTeamCreation";
import { StepIndicator } from "components/Auth/StepIndicator";
import { useUserOnboardingStepper } from "hooks/useUserOnboardingStepper";
import { useToast } from "hooks/useToast";
import { BackendError } from "types/utils/error";
import { useLocalStorage } from "hooks/useLocalStorage";
import { Button } from "components/DesignSystem/Button/Button";
import { RightArrow } from "components/icons/RightArrow";
import { CaretDown } from "components/icons/CaretDown";
import authContext from "jwt_context&axios/authContext";
import { ChatCartPaid } from "components/icons/Chat/ChatCartPaid";

export const BrandCreationForm = ({
  groupName = "",
  isFromAdmin = false,
  setEntityCreate,
  moveBack,
  moveNext,
}: {
  groupName?: string;
  isFromAdmin?: boolean;
  setEntityCreate?: (val: boolean) => void;
  moveBack?: () => void;
  moveNext?: () => void;
}) => {
  const { isUserTypeForeignCA } = useRoleBasedView();
  const { pathname } = useLocation();
  const isForeignServiceProviderFlow =
    pathname === "/profile" && isUserTypeForeignCA;
  const { currentStepNumber, canMoveNext } = useUserOnboardingStepper();
  const currentUser = JSON.parse(localStorage.getItem("currentUser") || "{}");
  const auth = useContext(authContext);
  const userIdentifier =
    auth?.authtoken?.uuid || currentUser?.uuid || "default";
  const history = useHistory();

  const { localData: savedBrandName, setItem: saveBrandName } =
    useLocalStorage<string>(`onboarding_brand_${userIdentifier}`, groupName);

  const [nameOfBrand, setNameOfBrand] = useState(savedBrandName || groupName);

  useEffect(() => {
    if (savedBrandName) {
      setNameOfBrand(savedBrandName);
    }
  }, [savedBrandName]);

  const handleSubmit = async (values: typeof initialValues) => {
    setNameOfBrand(values.brandName);
    saveBrandName(values.brandName);
    handleGoNext();
  };

  const handleGoBack = () => {
    moveBack?.();
    history.push("/phoneverify");
  };

  const handleGoNext = () => {
    setEntityCreate?.(true);
    moveNext?.();
  };

  const initialValues = {
    brandName: nameOfBrand,
  };

  return (
    <div className="t-flex t-flex-col t-justify-between t-flex-1">
      <div>
        {!isFromAdmin && (
          <>
            {!isForeignServiceProviderFlow && currentStepNumber && (
              <StepIndicator
                currentStep={currentStepNumber}
                totalSteps={4}
                label="Brand details"
              />
            )}

            <div className="t-mb-8">
              <div className="t-text-subtitle-lg t-text-text-100 t-mb-2">
                {isForeignServiceProviderFlow
                  ? "Organisation details"
                  : "Add your brand details"}
              </div>
              {isForeignServiceProviderFlow && (
                <div className="t-text-body-sm t-text-text-30">
                  Tell us about your organisation and what you do
                </div>
              )}
            </div>
          </>
        )}

        {isForeignServiceProviderFlow ? (
          <ServiceTeamCreation />
        ) : (
          <Formik
            onSubmit={handleSubmit}
            validationSchema={brandCreationSchema}
            initialValues={initialValues}
            validateOnBlur={false}
          >
            {({ errors }) => {
              const isError = Boolean(errors.brandName);
              let rightComponent = <></>;
              if (canMoveNext) {
                rightComponent = <ChatCartPaid />;
              }
              return (
                <Form className="all:unset t-flex t-flex-col t-gap-8">
                  {!isFromAdmin && (
                    <TextInput
                      label="Enter brand name"
                      customSize="regular"
                      name="brandName"
                      type="text"
                      placeholder="Eg. Acme"
                      rightComponent={rightComponent}
                      disabled={canMoveNext}
                      customType={isError ? "warning" : "success"}
                      required
                      note="Use the name you operate under, not the legal entity
                        name"
                    />
                  )}
                  {!canMoveNext && (
                    <Button type="submit" customType="primary">
                      Continue &nbsp;
                      <RightArrow />
                    </Button>
                  )}
                </Form>
              );
            }}
          </Formik>
        )}
      </div>
      <div className="t-flex-shrink-0 t-mt-auto t-flex t-justify-between t-pt-6 t-pb-20">
        <Button
          onClick={handleGoBack}
          customType="link_neutral"
          type="button"
          size="small"
        >
          <CaretDown className="t-rotate-90 t-mr-1" /> Go back
        </Button>
        {canMoveNext && (
          <Button
            onClick={handleGoNext}
            customType="link_neutral"
            type="button"
            size="small"
          >
            Go Next <CaretDown className="-t-rotate-90 t-ml-1" />
          </Button>
        )}
      </div>
    </div>
  );
};
